/**
 * Button primitive.
 *
 * Token-backed accessible button. No Radix dependency (Button does not
 * need headless behaviour — a native `<button>` already is the
 * keyboard/screen-reader semantics). The sanctioned focus treatment is
 * `focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring
 * focus-visible:outline-none`; the `ring-ring` colour resolves through
 * `--color-ring` in `tokens.css`, which tracks the brand-green primary
 * token (FR-033: every interactive element MUST present a visible focus
 * indicator meeting WCAG 2.1 AA contrast). Light scheme only in
 * Cluster 1; dark mode is deferred (see `tokens.css` for rationale).
 *
 * Variants (`primary`, `secondary`, `outline`), sizes (`xs`, `sm`, `md`), and
 * shapes (`standard`, `pill`) are expressed as plain lookup objects;
 * `class-variance-authority` is NOT installed and MUST NOT be added
 * without governance authorisation (T004 brief + D-043). `outline`
 * differs from `secondary` in fill: it paints the page background
 * (`bg-background`) inside a token-backed border so it reads as a
 * transparent-on-card outline button, where `secondary` paints the
 * faint surface fill (`bg-secondary`). Both keep the same border
 * treatment so the two variants line up vertically when stacked in an
 * action row. The `md` size hits 44px minimum height so it satisfies
 * FR-036 mobile tap-target minimums out of the box; `sm` is reserved
 * for inline / secondary surfaces where the larger tap target does
 * not apply (callers opting into `sm` accept the tap-target
 * responsibility at their surface). `xs` is the 32px (`min-h-6`)
 * compact pill used by the CMS shell's header cluster (Arkisto /
 * Päävalikko, Phase 19 first-screen parity) — below the tap-target
 * minimum, accepted at that desktop-only authoring-chrome surface (the
 * CMS shell is desktop-gated). NB: `min-h-6` is 32px because omaplus
 * retunes `--spacing-6` to 2rem; `min-h-8` would be 48px here. Every
 * class below is a token-backed Tailwind utility — no arbitrary-bracket
 * classes (FR-047), no raw colour literals (FR-002).
 *
 * The default `standard` shape uses `rounded-lg` (0.75rem — the
 * prototype's base radius from `tokens.css` `--radius-lg`) and is the
 * softened corner treatment the rest of the shell uses on cards and
 * surfaces. The `pill` shape uses `rounded-full` for the proto-aligned
 * fully-rounded action affordance (CMS editor's Save/Publish row,
 * future catalog row actions). `shape` is its own prop instead of a
 * caller `className` override because `cn()` is plain concatenation
 * (no `tailwind-merge`); same-property utility conflicts resolve by
 * Tailwind's stable compile order, NOT by className-string order, so a
 * caller-supplied `rounded-full` cannot reliably defeat a baseClass
 * `rounded-lg`. Exposing shape as a typed prop guarantees the chosen
 * radius wins because only one is emitted into the class list.
 */

import type { ButtonHTMLAttributes, Ref } from 'react';

import { cn } from './utils.js';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'destructive'
  | 'destructiveOutline'
  | 'ghost';
export type ButtonSize = 'xs' | 'sm' | 'md';
export type ButtonShape = 'standard' | 'pill';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  ref?: Ref<HTMLButtonElement>;
}

const baseClasses =
  'inline-flex items-center justify-center font-semibold ' +
  'transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const variantClasses = {
  primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
  secondary: 'bg-secondary text-secondary-foreground border border-border hover:bg-secondary/80',
  outline: 'bg-background text-foreground border border-border hover:bg-muted',
  // Destructive confirm actions (024-cms US6 archive confirm; the proto's
  // AlertDialogAction posture). Token-backed `--color-destructive[-foreground]`.
  destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  // The proto's OUTLINED light-red destructive — a destructive action that
  // sits IN a list of rows rather than at the end of a confirm dialog. The
  // filled `destructive` was right for the dialog's final commit and wrong
  // repeated down a catalog: seven published rows each carrying a solid red
  // button made the loudest thing on the surface the one action nobody came
  // to perform (owner, 2026-09-03). Outlined keeps the destructive READING —
  // red border, red text — without the filled weight.
  //
  // A named variant, not a class string at the call site: `cn()` is plain
  // concatenation, so a caller layering `border-destructive` over `destructive`
  // would resolve by Tailwind's compile order rather than by intent.
  destructiveOutline:
    'bg-background text-destructive border border-destructive hover:bg-destructive/10',
  // Chromeless icon/inline action (024-cms page-editor block header remove;
  // the proto's `variant="ghost"`). No fill, no border — transparent on its
  // surface with a faint hover wash so an icon-only affordance reads as a
  // button without adding a box. Icon tint (e.g. destructive) is supplied by
  // the caller on the glyph, so ghost stays colour-neutral.
  ghost: 'text-foreground hover:bg-muted',
} as const satisfies Record<ButtonVariant, string>;

const sizeClasses = {
  // 32px — the proto's compact header pill (`--spacing-6` = 2rem; `min-h-8`
  // would be 48px under omaplus's retuned scale).
  xs: 'min-h-6 px-3 py-1 text-sm',
  sm: 'min-h-9 px-3 py-1 text-sm',
  md: 'min-h-11 px-4 py-2 text-base',
} as const satisfies Record<ButtonSize, string>;

const shapeClasses = {
  standard: 'rounded-lg',
  pill: 'rounded-full',
} as const satisfies Record<ButtonShape, string>;

export function Button({
  variant = 'primary',
  size = 'md',
  shape = 'standard',
  type = 'button',
  className,
  ref,
  ...rest
}: ButtonProps) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        shapeClasses[shape],
        className,
      )}
      {...rest}
    />
  );
}
